import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import {
  Banner, Button, Dialog, List, ListItem, TextField, useSnackbar,
} from '@meshble/molo';
import { api, must, mustOk, type Instance } from '../lib/api';
import { quandoRelativo } from '../lib/attivita';
import { Anello } from '../brand';
import { Info } from '../icons';
import { localeIntl, t } from '../lib/lingua';
import { usePermessiSu } from '../session';
import { ConPermesso } from '../permesso';

/**
 * I repository di moduli collegati a un'istanza. È un pannello della sua
 * scheda, accanto agli indirizzi.
 *
 * Il collegamento non porta il codice sulla macchina: lo fa «Rigenera la
 * configurazione». La separazione è voluta e va detta a schermo, perché la
 * chiave va incollata su GitHub *prima* che il clone possa riuscire, e un
 * collegamento che tentasse subito fallirebbe sempre la prima volta.
 */

type Repo = { id: string; url: string; branch: string; subdir?: string; folder: string; publicKey?: string; public: boolean; lastCommit?: string; deployedAt?: string };
// Il segreto del webhook esiste solo nella risposta della creazione: nel
// contratto dell'elenco non c'è proprio, ed è il motivo per cui è un tipo a parte.
type RepoCreato = Repo & { webhookSecret: string };

/** I repository collegati a un'istanza: servono anche alla scheda, perché
 *  con dei repository riconfigurare porta codice nuovo (blocco 35). */
export function useRepoCollegati(slug: string, id: string) {
  return useQuery({
    queryKey: ['addons', slug, id],
    queryFn: () =>
      must(api.GET('/api/v1/orgs/{slug}/instances/{id}/addons', { params: { path: { slug, id } } })),
  });
}

export function Addon({ slug, istanza }: { slug: string; istanza: Instance }) {
  const qc = useQueryClient();
  const avvisa = useSnackbar();
  const navigate = useNavigate();

  const q = useRepoCollegati(slug, istanza.id);
  const permessi = usePermessiSu(slug, { istanza: istanza.id });
  const repos: Repo[] = q.data ?? [];

  const [apri, setApri] = useState(false);
  const [url, setUrl] = useState('');
  const [branch, setBranch] = useState('');
  const [subdir, setSubdir] = useState('');
  // La chiave appena generata, mostrata finché la scheda resta aperta: è il
  // momento in cui va incollata su GitHub, e mandarla a cercare altrove è il
  // modo più semplice di far dimenticare il passaggio.
  const [appena, setAppena] = useState<RepoCreato | Repo | null>(null);
  const [chiediEnterprise, setChiediEnterprise] = useState(false);
  const [chiediAggiornamento, setChiediAggiornamento] = useState(false);
  // Il repository di cui si sta cambiando il ramo, e il ramo nuovo.
  const [ramoDi, setRamoDi] = useState<Repo | null>(null);
  const [ramo, setRamo] = useState('');

  const invalida = () => qc.invalidateQueries({ queryKey: ['addons', slug, istanza.id] });

  const collega = useMutation({
    mutationFn: () =>
      must(
        api.POST('/api/v1/orgs/{slug}/instances/{id}/addons', {
          params: { path: { slug, id: istanza.id } },
          body: {
            url: url.trim(),
            ...(branch.trim() ? { branch: branch.trim() } : {}),
            ...(subdir.trim() ? { subdir: subdir.trim() } : {}),
          },
        }),
      ),
    onSuccess: (r) => {
      invalida();
      setApri(false);
      setUrl('');
      setBranch('');
      setSubdir('');
      setAppena(r as RepoCreato);
      avvisa(r.public
        ? t('Collegato: è pubblico, niente chiave da incollare. Portalo sul server')
        : t('Collegato. Incolla la chiave su GitHub, poi rigenera la configurazione'));
    },
  });

  const scollega = useMutation({
    mutationFn: (r: Repo) =>
      mustOk(
        api.DELETE('/api/v1/orgs/{slug}/instances/{id}/addons/{repoId}', {
          params: { path: { slug, id: istanza.id, repoId: r.id } },
        }),
      ),
    onSuccess: (_x, r) => {
      invalida();
      if (appena?.id === r.id) setAppena(null);
      avvisa(t('Scollegato {url}', { url: r.url }));
    },
  });

  const cambiaRamo = useMutation({
    mutationFn: (r: Repo) =>
      must(
        api.PATCH('/api/v1/orgs/{slug}/instances/{id}/addons/{repoId}', {
          params: { path: { slug, id: istanza.id, repoId: r.id } },
          body: { branch: ramo.trim() },
        }),
      ),
    onSuccess: (r) => {
      invalida();
      setRamoDi(null);
      avvisa(t('{url} ora segue {ramo}: portalo sul server', { url: r.url, ramo: r.branch }));
    },
  });

  const attiva = useMutation({
    mutationFn: () =>
      must(
        api.POST('/api/v1/orgs/{slug}/instances/{id}/enterprise', {
          params: { path: { slug, id: istanza.id } },
        }),
      ),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['instance', slug, istanza.id] });
      setChiediEnterprise(false);
      avvisa(t('Attivo Enterprise: l’istanza resta ferma un paio di minuti'));
      // Come per la riconfigurazione: è un job con i suoi passi, e si va dove
      // si vede invece di restare a guardare uno stato che non si muove.
      if (r.jobId) navigate({ to: '/orgs/$slug/jobs/$id', params: { slug, id: r.jobId } });
    },
  });

  const aggiorna = useMutation({
    mutationFn: () =>
      must(
        api.POST('/api/v1/orgs/{slug}/instances/{id}/update', {
          params: { path: { slug, id: istanza.id } },
          body: {},
        }),
      ),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['instance', slug, istanza.id] });
      setChiediAggiornamento(false);
      avvisa(t('Aggiorno Odoo: l’istanza resta ferma per qualche minuto'));
      if (r.jobId) navigate({ to: '/orgs/$slug/jobs/$id', params: { slug, id: r.jobId } });
    },
  });

  // La stessa riconfigurazione di «Rigenera la configurazione» nella scheda
  // Risorse: sta anche qui perché è qui che si legge «non ancora portato sul
  // server», e mandare a cercare il pulsante in un'altra scheda è il modo più
  // semplice di lasciare il repository collegato e mai clonato. Dopo il primo
  // clone diventa «Aggiorna»: lo step porta ogni repository alla punta del suo
  // ramo, che è la stessa cosa che fa una push col webhook.
  const porta = useMutation({
    mutationFn: () =>
      must(api.PATCH('/api/v1/orgs/{slug}/instances/{id}', { params: { path: { slug, id: istanza.id } }, body: {} })),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['instance', slug, istanza.id] });
      avvisa(t('Porto i moduli sul server: ci vuole un minuto'));
      if (r.jobId) navigate({ to: '/orgs/$slug/jobs/$id', params: { slug, id: r.jobId } });
    },
  });

  const pronto = url.trim().length > 4 && !collega.isPending;
  // Riconfigurare vuole un'istanza accesa: su una ferma il riavvio finale non
  // avrebbe niente da riavviare.
  const puoAttivare = istanza.status === 'running' || istanza.status === 'error';

  return (
    <div className="md-stack">
      {q.error && <Banner tone="error" title={t('Non riesco a leggere i repository')}>{q.error.message}</Banner>}
      {collega.error && <Banner tone="error" title={t('Non l’ho collegato')}>{(collega.error as Error).message}</Banner>}
      {porta.error && <Banner tone="error" title={t('Non l’ho portato sul server')}>{(porta.error as Error).message}</Banner>}

      {q.isSuccess && repos.length === 0 && !istanza.enterprise && (
        <p className="md-body-medium" style={{ margin: 0, color: 'var(--md-sys-color-on-surface-variant)' }}>
          {t('Nessun modulo aggiuntivo. Questa istanza ha solo il codice standard di Odoo.')}
        </p>
      )}

      <StatoCodice codice={istanza.code} major={istanza.odooMajor} />

      {istanza.enterprise ? (
        <>
          <p className="md-body-medium" style={{ margin: 0 }}>
            {t('Questa istanza porta anche {repo}, clonato con la credenziale dell’organizzazione.', { repo: <b>odoo/enterprise</b> })}
          </p>
          <Registrazione scadenza={istanza.enterpriseExpiry} />
        </>
      ) : (
        <p className="md-body-medium" style={{ margin: 0, color: 'var(--md-sys-color-on-surface-variant)' }}>
          {t('Solo codice Community. Enterprise si può accendere anche adesso: l’istanza resta ferma un paio di minuti, e non si spegne più.')}
        </p>
      )}

      {repos.length > 0 && (
        <List>
          {repos.map((r) => (
            <ListItem
              key={r.id}
              headline={r.url}
              supporting={
                [
                  t('ramo {ramo}', { ramo: r.branch }),
                  r.public ? t('pubblico, in https') : null,
                  r.subdir ? t('cartella {cartella}', { cartella: r.subdir }) : null,
                  !r.deployedAt
                    ? t('non ancora portato sul server')
                    : r.lastCommit
                      ? t('sul server da {quando} ({commit})', { quando: quandoRelativo(r.deployedAt), commit: r.lastCommit.slice(0, 7) })
                      : t('sul server da {quando}', { quando: quandoRelativo(r.deployedAt) }),
                ]
                  .filter(Boolean)
                  .join(' · ')
              }
              trailing={
                <span className="md-stack__inline">
                  <ConPermesso ha={permessi} serve={['instance.config.write', 'instance.code.pull']}>
                    <Button
                      variant={r.deployedAt ? 'text' : 'tonal'}
                      icon={porta.isPending ? <Anello /> : undefined}
                      disabled={!puoAttivare || porta.isPending}
                      onClick={() => porta.mutate()}
                    >
                      {r.deployedAt ? t('Aggiorna') : t('Portalo sul server')}
                    </Button>
                  </ConPermesso>
                  <ConPermesso ha={permessi} serve="instance.deploy">
                    <Button
                      variant="text"
                      onClick={() => {
                        cambiaRamo.reset();
                        setRamo(r.branch);
                        setRamoDi(r);
                      }}
                    >
                      {t('Cambia ramo')}
                    </Button>
                  </ConPermesso>
                  <ConPermesso ha={permessi} serve="instance.deploy">
                    <Button variant="text" disabled={scollega.isPending} onClick={() => scollega.mutate(r)}>
                      {t('Scollega')}
                    </Button>
                  </ConPermesso>
                </span>
              }
            />
          ))}
        </List>
      )}

      {appena && <ChiaveDaIncollare repo={appena} onChiudi={() => setAppena(null)} />}

      {attiva.error && (
        <Banner tone="error" title={t('Non ho acceso Enterprise')}>{(attiva.error as Error).message}</Banner>
      )}

      <div className="md-stack__inline">
        <ConPermesso ha={permessi} serve="instance.deploy">
          <Button variant="tonal" onClick={() => setApri(true)}>{t('Collega un repository')}</Button>
        </ConPermesso>
        {repos.some((r) => r.publicKey) && (
          <Button variant="text" onClick={() => setAppena(repos.find((r) => r.publicKey) ?? null)}>{t('Rivedi le chiavi')}</Button>
        )}
        <ConPermesso ha={permessi} serve="instance.update">
          <Button variant="text" disabled={!puoAttivare} onClick={() => setChiediEnterprise(true)}>
            {istanza.enterprise ? t('Rilancia l’installazione Enterprise') : t('Attiva Enterprise')}
          </Button>
        </ConPermesso>
        {(istanza.code?.behind ?? 0) > 0 && (
          <ConPermesso ha={permessi} serve="instance.update">
            <Button
              variant="text"
              disabled={istanza.status !== 'running'}
              onClick={() => setChiediAggiornamento(true)}
            >
              {t('Aggiorna Odoo')}
            </Button>
          </ConPermesso>
        )}
      </div>

      {aggiorna.error && (
        <Banner tone="error" title={t('Non ho aggiornato')}>{(aggiorna.error as Error).message}</Banner>
      )}

      <Dialog
        open={chiediAggiornamento}
        onOpenChange={(v) => !v && setChiediAggiornamento(false)}
        title={t('Aggiorna Odoo all’ultimo commit del ramo')}
        description={t('Stessa versione di Odoo, codice più recente. Community ed Enterprise si spostano insieme.')}
        actions={
          <>
            <Button variant="text" onClick={() => setChiediAggiornamento(false)}>{t('Annulla')}</Button>
            <Button
              variant="filled"
              icon={aggiorna.isPending ? <Anello /> : undefined}
              disabled={aggiorna.isPending}
              onClick={() => aggiorna.mutate()}
            >
              {aggiorna.isPending ? t('Avvio…') : t('Aggiorna')}
            </Button>
          </>
        }
      >
        <div className="md-stack">
          <p className="md-body-medium" style={{ margin: 0 }}>
            <b>{t('L’istanza resta ferma per qualche minuto.')}</b>{' '}
            {t('Aggiornare il codice di Odoo vuol dire far migrare il database, e la durata dipende da quanti dati ci sono. Prima prendo una copia del database sul disco del server.')}
          </p>
          <p className="md-body-medium" style={{ margin: 0 }}>
            {t('{forte} i due repository tornano dov’erano, il database si ricarica dalla copia e l’istanza si riaccende com’era. Quello che perdi è il tempo, non i dati.', {
              forte: <b>{t('Se va storto torna indietro da solo:')}</b>,
            })}
          </p>
          <p className="md-body-medium" style={{ margin: 0, color: 'var(--md-sys-color-on-surface-variant)' }}>
            {t('Fallo prima su un’istanza che puoi permetterti di fermare. Sono {n} commit, e nessuno li ha ancora provati su questo database.', { n: istanza.code?.behind ?? 0 })}
          </p>
        </div>
      </Dialog>

      <Dialog
        open={chiediEnterprise}
        onOpenChange={(v) => !v && setChiediEnterprise(false)}
        title={istanza.enterprise ? t('Rilancia l’installazione Enterprise') : t('Attiva Enterprise su questa istanza')}
        description={t('Porta il codice di odoo/enterprise sulla macchina, installa lo strato Enterprise dentro Odoo e riavvia. Non si torna indietro.')}
        actions={
          <>
            <Button variant="text" onClick={() => setChiediEnterprise(false)}>{t('Annulla')}</Button>
            <Button
              variant="filled"
              icon={attiva.isPending ? <Anello /> : undefined}
              disabled={attiva.isPending}
              onClick={() => attiva.mutate()}
            >
              {attiva.isPending ? t('Attivo…') : istanza.enterprise ? t('Rilancia') : t('Attiva Enterprise')}
            </Button>
          </>
        }
      >
        <div className="md-stack">
          {istanza.enterprise ? (
            <p className="md-body-medium" style={{ margin: 0 }}>
              {t('Questa istanza è già segnata Enterprise: rilanciare rifà il giro senza rifare i danni. Il codice si aggiorna invece di riclonarsi e un modulo già installato viene lasciato stare, quindi serve quando l’attivazione si è fermata a metà.')}
            </p>
          ) : (
            <p className="md-body-medium" style={{ margin: 0 }}>
              <b>{t('Non si spegne.')}</b>{' '}
              {t('Aggiungere Enterprise a un database Community è un’aggiunta di moduli e si fa senza problemi. Il contrario no: i moduli Enterprise installati restano scritti nel database, e un Odoo che li trova in tabella senza trovarne il codice non parte più.')}
            </p>
          )}
          <p className="md-body-medium" style={{ margin: 0 }}>
            <b>{t('L’istanza resta ferma un paio di minuti.')}</b>{' '}
            {t('Un’installazione scrive nel registro dei moduli, e non si può fare mentre Odoo gira. Prima prendo una copia del database sul disco del server: se qualcosa va storto, l’istanza si riaccende com’era.')}
          </p>
          <p className="md-body-medium" style={{ margin: 0, color: 'var(--md-sys-color-on-surface-variant)' }}>
            {t('Al termine l’interfaccia è quella Enterprise e le altre app sono nell’elenco, da installare quando servono. Il codice di sottoscrizione si mette dentro Odoo.')}
          </p>
        </div>
      </Dialog>

      <Dialog
        open={ramoDi !== null}
        onOpenChange={(v) => !v && setRamoDi(null)}
        title={t('Cambia il ramo')}
        description={ramoDi ? t('Da quale ramo di {url} questa istanza prende il codice.', { url: ramoDi.url }) : undefined}
        actions={
          <>
            <Button variant="text" onClick={() => setRamoDi(null)}>{t('Annulla')}</Button>
            <Button
              variant="filled"
              icon={cambiaRamo.isPending ? <Anello /> : undefined}
              disabled={!ramo.trim() || cambiaRamo.isPending}
              onClick={() => ramoDi && cambiaRamo.mutate(ramoDi)}
            >
              {t('Cambia')}
            </Button>
          </>
        }
      >
        <form
          className="md-form"
          onSubmit={(e) => {
            e.preventDefault();
            if (ramoDi && ramo.trim()) cambiaRamo.mutate(ramoDi);
          }}
        >
          {cambiaRamo.error && (
            <Banner tone="error" title={t('Non l’ho cambiato')}>{(cambiaRamo.error as Error).message}</Banner>
          )}
          <p className="md-body-medium" style={{ margin: 0 }}>
            {istanza.staging
              ? t('Il ramo cambia solo su questa copia: la produzione resta dov’è. È il modo di provare un ramo, per esempio develop, sui dati veri prima di portarlo dove lavorano i clienti.')
              : t('Il ramo cambia solo su questa istanza. Per provarne uno prima di metterlo davanti ai clienti, fallo su una copia di prova.')}
          </p>
          <TextField
            label={t('Ramo')}
            value={ramo}
            autoFocus
            onChange={(e) => setRamo(e.target.value)}
            help={t('Il codice arriva con «Portalo sul server», che aggiorna anche i moduli cambiati.')}
          />
        </form>
      </Dialog>

      <Dialog
        open={apri}
        onOpenChange={(v) => !v && setApri(false)}
        title={t('Collega un repository di moduli')}
        description={t('Un repository pubblico, come quelli di OCA, arriva in https senza chiavi. Per uno privato l’hub genera una chiave di sola lettura, o riusa quella che l’azienda ha già. Il codice arriva sulla macchina con «Portalo sul server». Per più istanze insieme c’è il Catalogo.')}
        actions={
          <>
            <Button variant="text" onClick={() => setApri(false)}>{t('Annulla')}</Button>
            <Button
              variant="filled"
              icon={collega.isPending ? <Anello /> : undefined}
              disabled={!pronto}
              onClick={() => collega.mutate()}
            >
              {t('Collega')}
            </Button>
          </>
        }
      >
        <form
          className="md-form"
          onSubmit={(e) => {
            e.preventDefault();
            if (pronto) collega.mutate();
          }}
        >
          <TextField
            label="Repository"
            value={url}
            autoFocus
            onChange={(e) => setUrl(e.target.value)}
            placeholder="github.com/acme/moduli"
            help={t('Va bene anche l’indirizzo https o ssh copiato da GitHub.')}
          />
          <TextField
            label={t('Ramo')}
            value={branch}
            onChange={(e) => setBranch(e.target.value)}
            placeholder={istanza.odooMajor}
            help={t('Vuoto: {major}, la major di questa istanza.', { major: istanza.odooMajor })}
          />
          <TextField
            label={t('Sottocartella')}
            value={subdir}
            onChange={(e) => setSubdir(e.target.value)}
            help={t('Solo se i moduli non stanno nella radice del repository.')}
          />
        </form>
      </Dialog>
    </div>
  );
}

/**
 * La chiave pubblica da incollare su GitHub.
 *
 * Non è un segreto — è la metà pubblica, ed è fatta per essere mostrata — ma è
 * il passaggio che, se salta, fa fallire il clone con un errore di
 * autenticazione che non dice quale sia il pezzo mancante.
 */
function ChiaveDaIncollare({ repo, onChiudi }: { repo: RepoCreato | Repo; onChiudi: () => void }) {
  const segreto = 'webhookSecret' in repo ? repo.webhookSecret : undefined;
  const avvisa = useSnackbar();
  // Un repository pubblico non ha chiave: resta solo il webhook, e solo
  // appena creato, perché il segreto non si rivede.
  if (!repo.publicKey && !segreto) return null;
  return (
    <Banner
      icon={<Info />}
      title={!repo.publicKey ? t('Se vuoi, il webhook') : segreto ? t('Due cose da incollare su GitHub') : t('Incolla questa chiave su GitHub')}
      action={
        <span className="md-stack__inline">
          {repo.publicKey && (
            <Button
              variant="text"
              onClick={() => {
                void navigator.clipboard?.writeText(repo.publicKey ?? '');
                avvisa(t('Chiave copiata'));
              }}
            >
              {t('Copia')}
            </Button>
          )}
          <Button variant="text" onClick={onChiudi}>{t('Fatto')}</Button>
        </span>
      }
    >
      <div className="md-stack">
        {repo.publicKey ? (
          <>
            <p style={{ margin: 0 }}>
              {t('Su {url}, in «Settings → Deploy keys → Add deploy key», {senza} spuntare «Allow write access». Finché non c’è, il clone fallisce.', {
                url: <b>{repo.url}</b>,
                senza: <b>{t('senza')}</b>,
              })}
            </p>
            <code
              className="md-mono"
              style={{ display: 'block', overflowX: 'auto', wordBreak: 'break-all', whiteSpace: 'pre-wrap' }}
            >
              {repo.publicKey}
            </code>
          </>
        ) : (
          <p style={{ margin: 0 }}>
            {t('{url} è pubblico: arriva sul server in https, senza chiavi. Il webhook serve solo se il repository è vostro e volete che una push aggiorni l’istanza da sola.', {
              url: <b>{repo.url}</b>,
            })}
          </p>
        )}

        {segreto && (
          <>
            <p style={{ margin: 0 }}>
              {t('E se vuoi che una push aggiorni l’istanza da sola, in «Settings → Webhooks → Add webhook»: indirizzo qui sotto, tipo di contenuto {tipo}, e il segreto nel campo «Secret». Il segreto non si rivede più.', {
                tipo: <b>application/json</b>,
              })}
            </p>
            <code
              className="md-mono"
              style={{ display: 'block', overflowX: 'auto', wordBreak: 'break-all', whiteSpace: 'pre-wrap' }}
            >
              {`${window.location.origin}/hooks/git/${repo.id}`}
              {'\n'}
              {segreto}
            </code>
          </>
        )}
      </div>
    </Banner>
  );
}


/**
 * La registrazione Enterprise del database, come la racconta Odoo.
 *
 * Non è un dettaglio da «Dettagli tecnici»: quando la data passa, Odoo mette
 * un banner rosso davanti ai clienti e dopo un po' limita. È il genere di cosa
 * che matura in silenzio per settimane, quindi si guarda da qui.
 */
function Registrazione({ scadenza }: { scadenza?: Instance['enterpriseExpiry'] }) {
  if (!scadenza) {
    return (
      <p className="md-body-small" style={{ margin: 0, color: 'var(--md-sys-color-on-surface-variant)' }}>
        {t('Registrazione non ancora letta: il controllo gira una volta al giorno.')}
      </p>
    );
  }
  if (!scadenza.date) {
    return (
      <Banner tone="error" title={t('Questo database non è registrato')}>
        {t('Odoo non ha una data di scadenza, che è come sta un database Enterprise prima che ci entri il codice di sottoscrizione. Si mette dentro Odoo, in Impostazioni.')}
      </Banner>
    );
  }
  const giorni = scadenza.days;
  const quando = new Date(scadenza.date).toLocaleDateString(localeIntl());
  const motivo = scadenza.reason ? ` ${t('Motivo dichiarato da Odoo: {motivo}.', { motivo: scadenza.reason })}` : '';
  if (giorni < 0) {
    return (
      <Banner tone="error" title={t('Registrazione scaduta il {data}', { data: quando })}>
        {t('Odoo si considera non registrato: mostra l’avviso ai tuoi clienti e col tempo limita.')}
        {motivo}
      </Banner>
    );
  }
  if (giorni <= 30) {
    return (
      <Banner tone="info" title={t('Registrazione in scadenza fra {n} giorni', { n: giorni })}>
        {t('Scade il {data}. Dopo, Odoo comincia ad avvisare dentro l’interfaccia.', { data: quando })}
        {motivo}
      </Banner>
    );
  }
  return (
    <p className="md-body-small" style={{ margin: 0, color: 'var(--md-sys-color-on-surface-variant)' }}>
      {t('Registrazione valida fino al {data}, fra {n} giorni.', { data: quando, n: giorni })}
      {scadenza.codeSet ? '' : ` ${t('Il codice di sottoscrizione non risulta impostato.')}`}
    </p>
  );
}


/**
 * Su quale Odoo gira l'istanza, e quanto è indietro.
 *
 * Il codice del core non si aggiorna da solo — è un aggiornamento minore di
 * Odoo, con backup e ritorno indietro, e sta nel blocco 9 — quindi qui si
 * dice soltanto la distanza. Serve a sapere quando quell'aggiornamento
 * comincia a servire, invece di scoprirlo da un difetto corretto sei mesi fa.
 */
function StatoCodice({ codice, major }: { codice?: Instance['code']; major: string }) {
  if (!codice) {
    return (
      <p className="md-body-small" style={{ margin: 0, color: 'var(--md-sys-color-on-surface-variant)' }}>
        {t('Non so ancora su che commit di Odoo gira: il controllo passa una volta al giorno.')}
      </p>
    );
  }
  const sha = <span className="md-mono">{codice.srcCommit ? codice.srcCommit.slice(0, 7) : '—'}</span>;
  if (codice.behind < 0) {
    return (
      <p className="md-body-small" style={{ margin: 0, color: 'var(--md-sys-color-on-surface-variant)' }}>
        {t('Odoo {major} al commit {sha}. Non sono riuscito a confrontarlo con il ramo: di solito è la macchina che non raggiunge GitHub.', { major, sha })}
      </p>
    );
  }
  if (codice.behind === 0) {
    return (
      <p className="md-body-small" style={{ margin: 0, color: 'var(--md-sys-color-on-surface-variant)' }}>
        {t('Odoo {major} aggiornato all’ultimo commit del ramo ({sha}).', { major, sha })}
      </p>
    );
  }
  return (
    <p className="md-body-small" style={{ margin: 0, color: 'var(--md-sys-color-on-surface-variant)' }}>
      {t('Odoo {major} al commit {sha}, indietro di {n} commit rispetto al ramo.', { major, sha, n: <b>{codice.behind}</b> })}
      {codice.enterpriseCommit ? ` ${t('Enterprise è agganciato allo stesso punto.')}` : ''}
      {' '}{t('Si aggiorna con «Aggiorna Odoo», qui sotto.')}
    </p>
  );
}
